import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from 'react'
import {
  ProTable,
  ProColumns,
  ActionType,
  ColumnsState,
} from '@ant-design/pro-components'
import { StandardFilter, StandardFilterField } from '../StandardFilter';
import { TablePagination } from '../TablePagination';
import { SummaryRow } from '../SummaryRow';
import { IconFont } from '../IconFont';
import { ModalFilter, FilterSchema } from '../ModalFilter';
import { message } from 'antd'
import { useDebounce, useSize, useUpdateEffect } from 'ahooks'
import { merge } from 'lodash'
import './style.less'

export interface PaginationConfig {
  current?: number
  pageSize?: number
  total?: number
  showSizeChanger?: boolean
  showQuickJumper?: boolean
  showTotal?: (total: number, range: [number, number]) => React.ReactNode

  [key: string]: any
}

export interface GatherTableProps<T = any> {
  /** 表格列配置 */
  columns: ProColumns<T>[]
  /** 数据请求方法 */
  request: (params: {
    page?: number
    pageSize?: number
    [key: string]: any
  }) => Promise<{
    data?: T[]
    total?: number
    statistics?: any
  }>
  /** 标准筛选器配置 */
  filters?: StandardFilterField[]
  /** 模态筛选器配置 */
  modalFilter?: FilterSchema[]
  /** 筛选器左侧渲染内容 */
  filterLeftRender?: React.ReactNode
  filterRightRender?: React.ReactNode
  /** 初始查询参数 */
  initialValue?: Record<string, any>
  /** 筛选器额外属性 */
  filterProps?: Record<string, any>
  // 格式化查询参数
  paramsFormat?: (params: any) => any
  /** 额外的查询参数 */
  params?: Record<string, any>
  /** 分页配置 */
  pagination?: PaginationConfig
  /** 筛选参数变化回调 */
  filtersChange?: (params: Record<string, any>) => void
  /** 多选变化回调 */
  selectionChange?: (selectedRows: T[]) => void
  /** 自定义批量操作渲染 */
  selection?: (selectedRows: T[]) => React.ReactNode
  // 回显勾选
  selectedRowsKey?: any[]

  /** ProTable 其他属性 */
  [key: string]: any
}

interface ColumnWithState<T = any> {
  col: ProColumns<T>
  show: boolean
  order: number
}

export interface GatherTableRef {
  /** 刷新表格 */
  refresh: () => void
  /** 重置表格参数 */
  reset: () => void
  /** 清空选中项 */
  clearSelected: () => void
}

const GatherTableInner = <T extends Record<string, any> = any>(
    props: GatherTableProps<T>,
    ref: React.Ref<GatherTableRef>,
) => {
  const {
    columns,
    request,
    // 查询表单
    filters,
    // 临时用参数区分，后续统一
    modalFilter,
    filterLeftRender,
    filterRightRender,
    // 查询表单初始参数
    initialValue = {},
    // 查询表单透传
    filterProps = {},
    // 查询参数
    params,
    paramsFormat,
    pagination,
    filtersChange,
    selectionChange,
    selection,
    ...tableProps
  } = props
  const actionRef = useRef<ActionType>(null)
  const outerRef = useRef<HTMLDivElement>(null)
  const rawSize = useSize(outerRef)
  const size = useDebounce(rawSize, { wait: 100 })

  /** 业务状态 */
  const [tableParams, setTableParams] = useState(initialValue)
  useEffect(() => {
    filtersChange?.(tableParams)
  }, [tableParams])
  const [summary, setSummary] = useState<any>(null)

  /** 列状态持久化 key */
  const _columnsStateKey =
      tableProps?.columnsState?.persistenceKey ||
      `gather-table-columns-${window.location.pathname}`

  /** 从 localStorage 恢复列状态 */
  const [columnsState, setColumnsState] = useState<
      Record<string, ColumnsState>
  >(() => {
    try {
      const saved = localStorage.getItem(_columnsStateKey)
      return saved ? JSON.parse(saved) : {}
    } catch (e) {
      console.warn('ColumnsState restore failed', e)
      return {}
    }
  })
  const [paginationObj, setPaginationObj] = useState<{
    current: number
    pageSize: number
    total: number
    [key: string]: any
  }>({ current: 1, pageSize: 20, total: 0, ...pagination })
  const handleColumnsStateChange = (state: Record<string, ColumnsState>) => {
    setColumnsState(state)
    try {
      localStorage.setItem(_columnsStateKey, JSON.stringify(state))
    } catch (e) {
      console.warn('ColumnsState save failed', e)
    }
  }
  const [selectedRow, setSelectedRow] = useState<T[]>([])
  useUpdateEffect(() => {
    selectionChange?.(selectedRow)
  }, [selectedRow])

  /** 暴露给外部方法 */
  useImperativeHandle(ref, () => ({
    refresh: () => actionRef.current?.reload(),
    reset: () => {
      setTableParams({ ...params, ...initialValue })
      setSelectedRow([]) // 重置时也清空选中项
      actionRef.current?.reset?.()
    },
    clearSelected: () => setSelectedRow([]),
  }))
  return (
      <div className="gather-table-outer w-full">
        {/* 中间区域：占满剩余高度 */}
        <div
            className="gather-table-body [&_.ant-table-cell-row-hover_.row-actions]:flex"
            ref={outerRef}
        >
          <ProTable<T>
              size="small"
              actionRef={actionRef}
              columns={columns}
              search={false}
              // 底部分页组件接管
              pagination={false}
              request={async (params) => {
                try {
                  // setSelectedRow([])
                  const res = await request(
                      paramsFormat ? paramsFormat(params) : params,
                  )
                  setSummary(res?.statistics || null)
                  setPaginationObj((prev) => ({
                    ...prev,
                    total: res?.total || 0,
                  }))
                  return {
                    data: res?.data || [],
                    total: res?.total || 0,
                    success: true,
                  }
                } catch (err: any) {
                  message.error(err?.message || '数据加载失败')
                  return { data: [], total: 0, success: false }
                }
              }}
              toolBarRender={() => [
                filters ? (
                    <StandardFilter
                        key="filter"
                        fields={filters}
                        leftRender={filterLeftRender}
                        onChange={(values) => {
                          setTableParams({ ...params, ...values })
                        }}
                        initialValues={tableParams}
                        {...filterProps}
                    />
                ) : (
                    <>
                      {filterLeftRender}
                      <ModalFilter
                          columns={modalFilter || []}
                          value={tableParams}
                          onChange={(values) =>
                              setTableParams({ ...params, ...values })
                          }
                          {...filterProps}
                      />
                      {filterRightRender}
                    </>
                ),
              ]}
              options={{
                fullScreen: false,
                reload: true,
                reloadIcon: (
                    <IconFont
                        type="icon-arrow-rotate-left-right"
                        className="settings-icon ml-[4px]"
                    />
                ),
                setting: {
                  settingIcon: (
                      <IconFont
                          type="icon-settings-gear-3"
                          className="settings-icon"
                      />
                  ),
                },
                density: false,
              }}
              summary={() => {
                const displayedColumns = columns
                .map((col: ProColumns<T>): ColumnWithState<T> => {
                  const key = String(col.dataIndex ?? col.key)
                  const state = columnsState[key]
                  return {
                    col,
                    show: state?.show !== false,
                    order: state?.order ?? 9999,
                  }
                })
                .filter((item: ColumnWithState<T>) => item.show)
                .sort(
                    (
                        a: ColumnWithState<T>,
                        b: ColumnWithState<T>,
                    ) => a.order - b.order,
                )
                .map((item: ColumnWithState<T>) => item.col)

                const finalColumns = selectionChange
                    ? [
                      {
                        dataIndex: undefined,
                        key: 'selection-placeholder',
                      },
                      ...displayedColumns,
                    ]
                    : displayedColumns

                return finalColumns.length > 0 && summary ? (
                    <SummaryRow
                        columns={finalColumns}
                        summaryData={summary}
                    />
                ) : null
              }}
              {...merge(
                  {
                    columnsState: {
                      persistenceKey: _columnsStateKey,
                      value: columnsState,
                      onChange: handleColumnsStateChange,
                    },
                    scroll: {
                      x: 'max-content',
                      y: size?.height ? size.height - 110 : 0,
                    },
                    params: {
                      ...params,
                      ...tableParams,
                      current: paginationObj?.current,
                      pageSize: paginationObj?.pageSize,
                    },
                    style: { height: size?.height || 0 },
                    sticky: true,
                    rowKey: 'id',
                    // 如果传入了 selectionChange 或 selection，则开启多选
                    ...(selectionChange || selection
                        ? {
                          rowSelection: {
                            selectedRowKeys:
                                props?.selectedRowsKey ||
                                selectedRow.map((row: T) => {
                                  const rowKeyField =
                                      tableProps?.rowKey || 'id'
                                  return typeof rowKeyField ===
                                  'function'
                                      ? rowKeyField(row)
                                      : row[rowKeyField]
                                }),
                            onChange: (
                                _: any,
                                selectedRows: T[],
                            ) => {
                              setSelectedRow(selectedRows)
                            },
                          },
                          tableAlertRender: () => false,
                        }
                        : {}),
                    tableLayout: "fixed"
                  },
                  tableProps,
              )}
          />
        </div>

        {/* 底部分页 */}
        <div className="gather-table-footer flex justify-between border-t">
          <div className="h-full py-[8px] pl-[16px]">
            {!!selectedRow.length && selection && (
                <div className="h-full px-[16px] flex items-center rounded-[16px] bg-[#F5F5F5]">
                  <div className="text-[#0077FD] font-14 font-medium mr-[56px]">
                    {`已选${selectedRow.length}项`}
                  </div>
                  <div className="flex items-center gap-2">
                    {React.Children.toArray(selection(selectedRow))}
                  </div>
                </div>
            )}
          </div>
          <div className="w-[700px]">
            <TablePagination
                pagination={{
                  onChange: (page, pageSize) => {
                    setPaginationObj((prev) => ({
                      ...prev,
                      current: page,
                      pageSize,
                    }))
                  },
                  ...paginationObj,
                }}
            />
          </div>
        </div>
      </div>
  )
}

const GatherTableWithRef = forwardRef(GatherTableInner as any) as <
        T extends Record<string, any> = any,
    >(
        props: GatherTableProps<T> & { ref?: React.Ref<GatherTableRef> },
    ) => React.ReactElement

;(GatherTableWithRef as any).displayName = 'GatherTable'

export const GatherTable = GatherTableWithRef
